import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      await authAPI.verifyResetToken(token);
      setTokenValid(true);
    } catch {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      toast.success('Password reimpostata con successo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel reset');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-center mb-8">
          <div className="w-14 h-14 rounded-lg overflow-hidden flex items-center justify-center mr-3">
            <img src="/obc-logo.png" alt="OBC Cloud" className="w-12 h-12 object-contain" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>OBC Cloud</h1>
        </div>

        <Card className="border-border bg-card">
          {!token || !tokenValid ? (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <XCircle className="w-5 h-5 text-destructive" />
                  Link Non Valido
                </CardTitle>
                <CardDescription>
                  Il link per reimpostare la password non e' valido o e' scaduto.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    I link di reset scadono dopo 1 ora. Richiedi un nuovo link.
                  </p>
                  <Link to="/forgot-password">
                    <Button className="w-full bg-primary text-primary-foreground">
                      Richiedi Nuovo Link
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="outline" className="w-full">
                      Torna al Login
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </>
          ) : success ? (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Password Reimpostata
                </CardTitle>
                <CardDescription>
                  La tua password e' stata aggiornata con successo.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center py-6">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                  <p className="text-center text-muted-foreground mb-6">
                    Ora puoi accedere con la tua nuova password.
                  </p>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-primary text-primary-foreground"
                    data-testid="go-to-login"
                  >
                    Vai al Login
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
                  Nuova Password
                </CardTitle>
                <CardDescription>
                  Inserisci la tua nuova password (minimo 6 caratteri)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Nuova Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        data-testid="new-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Minimo 6 caratteri"
                        className="pl-10 pr-10"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Conferma Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirm-password"
                        data-testid="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Ripeti la password"
                        className="pl-10"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="reset-submit"
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    disabled={loading}
                  >
                    {loading ? 'Salvataggio...' : 'Reimposta Password'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
